import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Clock,
  Eye,
  Share2,
  MessageCircle,
  Phone,
  MapPin,
  Send,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import SeoHead from '../../components/seo/SeoHead';
import { shareToSocial } from '../../lib/socialShare';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const API_ORIGIN = process.env.REACT_APP_BACKEND_URL; // sans /api pour les URLs publiques
const SITE_URL = 'https://phciesada.org';

const readingTime = (html) => {
  const text = (html || '').replace(/<[^>]*>/g, ' ');
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
};

/** Sanitize raw article HTML so admins can paste full HTML documents safely.
 *  - Strips DOCTYPE / <html> / <head> / <title> / <meta> / <link> / <script>
 *  - Strips <style> blocks (they break the magazine layout; prose CSS handles styling)
 *  - Extracts <body>...</body> content when present
 *  - Decodes entities like &lt;p&gt; if the content is double-escaped
 */
const sanitizeArticleHtml = (raw) => {
  if (!raw) return '';
  let html = String(raw);

  // Detect double-encoded HTML (admin pasted as text into a "code" editor)
  if (!/<\w+/.test(html) && /&lt;\w+/.test(html)) {
    html = html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  // Drop dangerous / disruptive blocks
  html = html.replace(/<!DOCTYPE[^>]*>/gi, '');
  html = html.replace(/<\?xml[^?]*\?>/gi, '');
  html = html.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
  html = html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
  html = html.replace(/<link\b[^>]*\/?>/gi, '');
  html = html.replace(/<meta\b[^>]*\/?>/gi, '');
  html = html.replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, '');

  // If a full document, extract only the body content
  const bodyMatch = html.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) html = bodyMatch[1];

  // Strip leftover <html>, <head>, <body> tags (keeping their inner content)
  html = html.replace(/<\/?(html|head|body)\b[^>]*>/gi, '');

  return html.trim();
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * BlogArticlePage — single article reader.
 * Pulls by slug from URL (falls back to article_id for legacy shares).
 * Includes Schema.org Article structured data, share buttons, related articles
 * and a pharmacy CTA block.
 */
const BlogArticlePage = ({ onBack, onNavigate, slug, fallbackId }) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [comment, setComment] = useState({ author_name: '', author_email: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchArticle = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      let res;
      if (slug) {
        res = await axios.get(`${API}/blog/articles/by-slug/${encodeURIComponent(slug)}`);
      } else if (fallbackId) {
        res = await axios.get(`${API}/blog/articles/${fallbackId}`);
      } else {
        setNotFound(true);
        return;
      }
      setArticle(res.data);
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug, fallbackId]);

  useEffect(() => {
    fetchArticle();
    window.scrollTo(0, 0);
  }, [fetchArticle]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.author_name.trim() || !comment.content.trim()) {
      toast.error('Nom et commentaire requis');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/blog/articles/${article.id}/comments`, comment);
      toast.success('Commentaire envoyé — il sera visible après modération');
      setComment({ author_name: '', author_email: '', content: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'envoyer le commentaire");
    } finally {
      setSubmitting(false);
    }
  };

  // URL clean de l'article (telle qu'elle s'ouvre dans la barre du navigateur)
  const shareUrl = article
    ? `${SITE_URL}/blog/${article.slug || article.id}`
    : SITE_URL;
  // URL « OG » côté backend : renvoie un HTML server-side rendered avec
  // les balises og:title/description/image afin que WhatsApp, Facebook,
  // Twitter… affichent un APERÇU correct (titre + image + résumé) au lieu
  // de la page d'accueil. Les humains sont redirigés instantanément vers
  // l'URL propre de l'article (`shareUrl`).
  // On utilise `window.location.origin` (et non REACT_APP_BACKEND_URL) :
  // si l'utilisateur est sur phciesada.org, on partage phciesada.org ; sur
  // preview, on partage preview. Ainsi l'article existe toujours dans la
  // DB correspondante et le scraper WhatsApp scrape les bonnes balises.
  const ORIGIN = typeof window !== 'undefined' ? window.location.origin : API_ORIGIN;
  const ogPreviewUrl = article?.slug
    ? `${ORIGIN}/api/blog/og/${article.slug}`
    : shareUrl;

  const share = (channel) => {
    const title = article?.title || 'Article';
    return shareToSocial(channel, {
      url: shareUrl,
      ogUrl: ogPreviewUrl,
      title,
      subject: `${title} — Pharmacie Saint Ange Des Amis`,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500">Chargement…</div>
    );
  }
  if (notFound || !article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
        <SeoHead title="Article introuvable" noIndex />
        <p className="text-xl text-neutral-700 mb-2">Article introuvable</p>
        <p className="text-neutral-500 mb-4">Cet article a peut-être été retiré ou déplacé.</p>
        <Button onClick={() => onNavigate('blog')} className="bg-[#C45C26] hover:bg-[#A64C1F] text-white">
          Retour au blog
        </Button>
      </div>
    );
  }

  const structured = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image_url ? [article.image_url] : undefined,
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at,
    author: {
      '@type': 'Organization',
      name: 'Pharmacie Saint Ange Des Amis',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pharmacie Saint Ange Des Amis',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/icons/icon-512x512.png`,
      },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': shareUrl },
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={article.title}
        description={article.excerpt || article.title}
        canonical={`/blog/${article.slug || article.id}`}
        image={article.image_url || undefined}
        type="article"
        structuredData={structured}
      />

      <header className="border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => onNavigate('blog')} className="-ml-3" data-testid="article-back-to-blog">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour au blog
          </Button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-neutral-900 leading-tight mb-4">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500 mb-8">
          <span>{formatDate(article.published_at || article.created_at)}</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {readingTime(article.content)} min de lecture</span>
          <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {article.views || 0} vues</span>
          {article.author_name && <span>· Par {article.author_name}</span>}
        </div>

        {article.image_url && (
          <div className="bg-neutral-100 rounded-2xl overflow-hidden mb-10 flex items-center justify-center" style={{ maxHeight: '600px' }}>
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-auto max-h-[600px] object-contain"
            />
          </div>
        )}

        <div
          className="prose prose-neutral max-w-none prose-headings:font-serif prose-a:text-[#C45C26] prose-strong:text-neutral-900"
          dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(article.content) }}
          onClick={(e) => {
            // Intercept internal blog links: /blog/<slug> → SPA navigation.
            // External links & anchors (#) garder le comportement natif.
            const anchor = e.target.closest('a');
            if (!anchor) return;
            const href = anchor.getAttribute('href') || '';
            if (!href) return;
            const match = href.match(/^(?:https?:\/\/[^/]+)?\/blog\/([^/?#]+)\/?(?:[?#].*)?$/i);
            if (!match) return;
            const targetSlug = decodeURIComponent(match[1]);
            if (!targetSlug || targetSlug === (article?.slug || '')) return;
            e.preventDefault();
            onNavigate('blog-article', { slug: targetSlug });
          }}
        />

        {/* Additional images gallery (images uploaded beyond the cover) */}
        {Array.isArray(article.images) && article.images.length > 1 && (
          <div className="mt-10" data-testid="blog-article-gallery">
            <h3 className="text-lg font-serif font-bold text-neutral-900 mb-4">
              Galerie photo
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {article.images.slice(1).map((img, idx) => (
                <div
                  key={img.id || idx}
                  className="bg-neutral-100 rounded-xl overflow-hidden flex items-center justify-center"
                  style={{ maxHeight: '500px' }}
                >
                  <img
                    src={img.url}
                    alt={img.caption || `${article.title} - image ${idx + 2}`}
                    className="w-full h-auto max-h-[500px] object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Share bar */}
        <div className="mt-10 pt-6 border-t border-neutral-200 flex flex-wrap items-center gap-3" data-testid="blog-article-share-bar">
          <span className="text-sm text-neutral-600 flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Partager :
          </span>
          {typeof navigator !== 'undefined' && navigator.share && (
            <Button variant="outline" size="sm" onClick={() => share('native')} className="text-[#0EA5E9] border-sky-200" data-testid="blog-share-native">
              <Share2 className="w-4 h-4 mr-1" /> Partager
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => share('whatsapp')} className="text-green-600 border-green-200" data-testid="blog-share-whatsapp">
            WhatsApp
          </Button>
          <Button variant="outline" size="sm" onClick={() => share('facebook')} className="text-blue-600 border-blue-200" data-testid="blog-share-facebook">
            Facebook
          </Button>
          <Button variant="outline" size="sm" onClick={() => share('instagram')} className="text-pink-600 border-pink-200 hover:bg-pink-50" data-testid="blog-share-instagram">
            Instagram
          </Button>
          <Button variant="outline" size="sm" onClick={() => share('tiktok')} className="text-neutral-900 border-neutral-300 hover:bg-neutral-100" data-testid="blog-share-tiktok">
            TikTok
          </Button>
          <Button variant="outline" size="sm" onClick={() => share('linkedin')} className="text-[#0A66C2] border-[#0A66C2]/30 hover:bg-[#0A66C2]/10" data-testid="blog-share-linkedin">
            LinkedIn
          </Button>
          <Button variant="outline" size="sm" onClick={() => share('twitter')} data-testid="blog-share-twitter">Twitter</Button>
          <Button variant="outline" size="sm" onClick={() => share('copy')} data-testid="blog-share-copy">Copier le lien</Button>
        </div>

        {/* Pharmacy CTA */}
        <div className="mt-10 bg-gradient-to-br from-[#C45C26] to-[#A64C1F] text-white rounded-2xl p-6 md:p-8">
          <h3 className="text-xl md:text-2xl font-serif font-bold mb-2">
            Besoin d&apos;un conseil personnalisé ?
          </h3>
          <p className="text-white/90 mb-4 leading-relaxed">
            Notre équipe jeune et dynamique vous accueille à la Pharmacie Saint Ange Des Amis,
            Angré 7<sup>ème</sup> Tranche, Carrefour Bluetooth. Hall aéré, espace de
            confidentialité d&apos;excellent standing, et parapharmacie fournie.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild className="bg-white text-[#C45C26] hover:bg-amber-50">
              <a href="tel:+2250778415277"><Phone className="w-4 h-4 mr-2" />07 78 41 52 77</a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white/10">
              <a href="https://wa.me/2250778415277" target="_blank" rel="noopener noreferrer">WhatsApp</a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white/10">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=5.37361,-3.98139"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MapPin className="w-4 h-4 mr-2" /> Itinéraire
              </a>
            </Button>
          </div>
        </div>

        {/* Related */}
        {Array.isArray(article.related) && article.related.length > 0 && (
          <section className="mt-12">
            <h3 className="text-xl font-serif font-bold text-neutral-900 mb-4">À lire aussi</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {article.related.map((r) => (
                <div
                  key={r.id}
                  className="cursor-pointer group"
                  onClick={() => onNavigate('blog-article', { slug: r.slug, fallbackId: r.id })}
                >
                  <div className="aspect-[4/3] bg-neutral-100 rounded-lg overflow-hidden mb-2">
                    {r.image_url ? (
                      <img src={r.image_url} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-300 text-3xl">📰</div>
                    )}
                  </div>
                  <h4 className="text-sm font-semibold text-neutral-900 group-hover:text-[#C45C26] line-clamp-2">{r.title}</h4>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Comments */}
        <section className="mt-12">
          <h3 className="text-xl font-serif font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" /> Commentaires ({article.comments?.length || 0})
          </h3>

          <form onSubmit={submitComment} className="space-y-3 mb-6">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                placeholder="Votre nom *"
                value={comment.author_name}
                onChange={(e) => setComment({ ...comment, author_name: e.target.value })}
                required
              />
              <Input
                type="email"
                placeholder="Email (optionnel, non publié)"
                value={comment.author_email}
                onChange={(e) => setComment({ ...comment, author_email: e.target.value })}
              />
            </div>
            <Textarea
              placeholder="Votre commentaire…"
              rows={4}
              value={comment.content}
              onChange={(e) => setComment({ ...comment, content: e.target.value })}
              required
            />
            <Button type="submit" disabled={submitting} className="bg-[#C45C26] hover:bg-[#A64C1F] text-white">
              <Send className="w-4 h-4 mr-2" />
              {submitting ? 'Envoi…' : 'Publier mon commentaire'}
            </Button>
            <p className="text-xs text-neutral-500">
              Les commentaires sont modérés avant publication.
            </p>
          </form>

          <div className="space-y-4">
            {(article.comments || []).map((c, i) => (
              <div key={i} className="border-l-2 border-[#C45C26]/30 pl-4">
                <p className="font-semibold text-neutral-900">{c.author_name}</p>
                <p className="text-xs text-neutral-500 mb-1">{formatDate(c.created_at)}</p>
                <p className="text-neutral-700 whitespace-pre-wrap">{c.content}</p>
              </div>
            ))}
            {(!article.comments || article.comments.length === 0) && (
              <p className="text-sm text-neutral-500 italic">Aucun commentaire pour l&apos;instant. Soyez le premier !</p>
            )}
          </div>
        </section>
      </article>
    </div>
  );
};

export default BlogArticlePage;
